import { registerSlider } from '@theme/lib-swiper-manager';
import { shouldReduceMotion } from '@theme/utilities';

const DEFAULT_TRANSITION_DURATION = 550;
const REDUCED_MOTION_TRANSITION_DURATION = 300;
const INTERACTION_RESUME_DELAY = 1200;

/**
 * @param {number} number
 * @param {number} total
 * @returns {string}
 */
function formatSlideStatus(number, total) {
  const template =
    Theme.translations.announcement_bar_slide_status ??
    'Slide {{ number }} of {{ total }}';
  return template
    .replace('{{ number }}', String(number))
    .replace('{{ total }}', String(total));
}

/**
 * @param {Element} swiperEl
 * @returns {Element[]}
 */
function getRealSlides(swiperEl) {
  return Array.from(
    swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
  ).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
}

/**
 * @param {Element} swiperEl
 */
function prepareSlideElements(swiperEl) {
  const wrapper = swiperEl.querySelector('.swiper-wrapper');
  if (!wrapper) return;

  wrapper.querySelectorAll(':scope > .shopify-block').forEach(function (slide) {
    slide.classList.add('swiper-slide', 'autoscroll-block__slide');
  });
}

/**
 * @param {Element} swiperEl
 * @param {HTMLElement} rootEl
 * @returns {object}
 */
registerSlider('[data-autoscroll-block]', function (swiperEl, rootEl) {
  prepareSlideElements(swiperEl);

  const duration = Number(rootEl.dataset.duration) || 4000;
  const shouldAutoplay =
    rootEl.dataset.autoRotate !== 'false' &&
    !shouldReduceMotion() &&
    !window.Shopify?.designMode;
  const pauseOnHover = rootEl.dataset.pauseOnHover !== 'false';
  const pauseOnFocus = rootEl.dataset.pauseOnFocus !== 'false';
  const transitionDuration = shouldReduceMotion()
    ? REDUCED_MOTION_TRANSITION_DURATION
    : DEFAULT_TRANSITION_DURATION;

  const prevButton = rootEl.querySelector('.slideshow__prev');
  const nextButton = rootEl.querySelector('.slideshow__next');

  let slides = getRealSlides(swiperEl);
  let currentIndex = slides.findIndex((slide) =>
    slide.classList.contains('is-active')
  );
  if (currentIndex < 0) currentIndex = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let interactionResumeTimeout = null;
  /** @type {SwiperInstance | null} */
  let swiper = null;

  const abortController = new AbortController();
  const { signal } = abortController;

  rootEl.style.setProperty(
    '--slideshow-animation-duration',
    `${transitionDuration}ms`
  );

  const hasMultipleSlides = slides.length > 1;

  /**
   * @returns {SwiperAutoplay | null}
   */
  function getControllableAutoplay() {
    if (!swiper?.autoplay || !shouldAutoplay || slides.length <= 1) return null;
    return swiper.autoplay;
  }

  function canControlAutoplay() {
    return getControllableAutoplay() !== null;
  }

  function pauseAutoplay() {
    const autoplay = getControllableAutoplay();
    if (!autoplay) return;
    if (autoplay.running && !autoplay.paused) {
      autoplay.pause();
    }
  }

  function resumeAutoplay() {
    const autoplay = getControllableAutoplay();
    if (!autoplay) return;
    if (pauseOnHover && rootEl.matches(':hover')) return;
    if (pauseOnFocus && rootEl.matches(':focus-within')) return;

    if (autoplay.paused && typeof autoplay.resume === 'function') {
      autoplay.resume();
      return;
    }

    if (!autoplay.running && typeof autoplay.start === 'function') {
      autoplay.start();
    }
  }

  function clearInteractionResume() {
    if (interactionResumeTimeout) {
      clearTimeout(interactionResumeTimeout);
      interactionResumeTimeout = null;
    }
  }

  function scheduleInteractionResume() {
    if (!canControlAutoplay()) return;
    clearInteractionResume();
    interactionResumeTimeout = setTimeout(function () {
      resumeAutoplay();
    }, INTERACTION_RESUME_DELAY);
  }

  function updateNavigationState() {
    const shouldHideNavigation = slides.length <= 1;

    if (prevButton instanceof HTMLButtonElement) {
      prevButton.hidden = shouldHideNavigation;
      prevButton.disabled = shouldHideNavigation;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.hidden = shouldHideNavigation;
      nextButton.disabled = shouldHideNavigation;
    }
  }

  /**
   * @param {number} from
   * @param {number} to
   */
  function dispatchSlideChange(from, to) {
    rootEl.dispatchEvent(
      new CustomEvent('slideshow:change', {
        detail: {
          currentIndex: to,
          previousIndex: from,
          direction: to >= from ? 'forward' : 'backward',
        },
      })
    );
  }

  /**
   * @param {number} progress
   */
  function updateProgress(progress) {
    rootEl.dispatchEvent(
      new CustomEvent('slideshow:progress', {
        detail: { progress },
      })
    );
  }

  function updateSlideStates() {
    slides = getRealSlides(swiperEl);

    slides.forEach(function (slide, index) {
      const isActive = index === currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute(
        'aria-label',
        formatSlideStatus(index + 1, slides.length)
      );
    });
  }

  /**
   * @param {Event} event
   */
  var handleKeydown = function (event) {
    if (!swiper || slides.length <= 1) return;
    if (!(event instanceof KeyboardEvent)) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        swiper.slidePrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        swiper.slideNext();
        break;
      case 'Home':
        event.preventDefault();
        swiper.slideToLoop(0);
        break;
      case 'End':
        event.preventDefault();
        swiper.slideToLoop(slides.length - 1);
        break;
    }
  };

  var handleMouseEnter = function () {
    clearInteractionResume();
    pauseAutoplay();
  };

  var handleMouseLeave = function () {
    scheduleInteractionResume();
  };

  var handleFocusIn = function () {
    clearInteractionResume();
    pauseAutoplay();
  };

  /**
   * @param {Event} event
   */
  var handleFocusOut = function (event) {
    const focusEvent = /** @type {FocusEvent} */ (event);
    if (
      focusEvent.relatedTarget &&
      focusEvent.relatedTarget instanceof Node &&
      rootEl.contains(focusEvent.relatedTarget)
    ) {
      return;
    }

    scheduleInteractionResume();
  };

  var handleVisibilityChange = function () {
    if (document.hidden) {
      pauseAutoplay();
    } else {
      resumeAutoplay();
    }
  };

  rootEl.addEventListener('keydown', handleKeydown, { signal });

  if (pauseOnHover) {
    rootEl.addEventListener('mouseenter', handleMouseEnter, { signal });
    rootEl.addEventListener('mouseleave', handleMouseLeave, { signal });
  }

  if (pauseOnFocus) {
    rootEl.addEventListener('focusin', handleFocusIn, { signal });
    rootEl.addEventListener('focusout', handleFocusOut, { signal });
  }

  document.addEventListener('visibilitychange', handleVisibilityChange, {
    signal,
  });

  const initialIndex = Math.min(currentIndex, Math.max(slides.length - 1, 0));
  const previousIndex = currentIndex;

  return {
    slidesPerView: 1,
    slideClass: 'shopify-block',
    loop: hasMultipleSlides,
    speed: transitionDuration,
    autoHeight: false,
    watchOverflow: true,
    observer: true,
    allowTouchMove: hasMultipleSlides,
    grabCursor: hasMultipleSlides,
    navigation:
      prevButton && nextButton
        ? {
            prevEl: prevButton,
            nextEl: nextButton,
          }
        : { enabled: false },
    autoplay:
      shouldAutoplay && hasMultipleSlides
        ? {
            delay: duration,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }
        : false,
    a11y: { enabled: false },
    on: {
      beforeInit: function () {
        prepareSlideElements(swiperEl);
        swiperEl.classList.remove('autoscroll-block--pre-init');
        swiperEl.classList.add('autoscroll-block--ready');
      },
      /**
       * @param {SwiperInstance} sw
       */
      init: function (sw) {
        swiper = sw;
        slides = getRealSlides(swiperEl);

        if (initialIndex > 0 && sw.realIndex !== initialIndex) {
          sw.slideToLoop(initialIndex, 0, false);
        }

        updateNavigationState();
        updateSlideStates();
        updateProgress(0);

        if (previousIndex !== currentIndex) {
          dispatchSlideChange(previousIndex, currentIndex);
        }
      },
      /**
       * @param {SwiperInstance} _sw
       * @param {number} _timeLeft
       * @param {number} percentage
       */
      autoplayTimeLeft: function (_sw, _timeLeft, percentage) {
        updateProgress((1 - percentage) * 100);
      },
      /**
       * @param {SwiperInstance} sw
       */
      slideChange: function (sw) {
        const from = currentIndex;
        currentIndex = sw.realIndex;
        updateNavigationState();
        updateSlideStates();
        dispatchSlideChange(from, currentIndex);
      },
      destroy: function () {
        abortController.abort();
        clearInteractionResume();
        swiperEl.classList.remove('autoscroll-block--ready');
        swiperEl.classList.add('autoscroll-block--pre-init');
        swiper = null;
      },
    },
  };
});
